'use client';

import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';

import { logger } from '@/lib/logger';

import { experimentsSelectors, useAppStore } from '@/store/app';

import { SUPPORTED_FEATURE_FLAGS } from '@/utils/featureFlags';

/** Hook to determine feature availability based on cookies or experiments */
export function useFeature(flag: string) {
  const [featureEnabled, setFeatureEnabled] = useState<boolean>(false);
  const { experiments } = useAppStore(experimentsSelectors);

  useEffect(() => {
    if (!SUPPORTED_FEATURE_FLAGS.includes(flag)) {
      logger.warn(`Unsupported feature flag: "${flag}"`);
      return;
    }

    const { search } = window.location;
    const enableFeature = search?.includes(`${flag}=true`);
    const disableFeature = search?.includes(`${flag}=false`);
    const hasQueryParam = search?.includes(`${flag}=`);
    const toggleOnPersisted = Cookies.get(flag) === 'true';

    // Example of how to enable a feature flag by default
    // if (featureFlag === YOUR_FLAG_NAME && (!search || !hasQueryParam)) {
    //   setFeatureEnabled(true);
    // }

    if (disableFeature) {
      Cookies.remove(flag);
      setFeatureEnabled(false);
    } else if (enableFeature || toggleOnPersisted) {
      Cookies.set(flag, 'true', { expires: 14 });
      setFeatureEnabled(true);
    } else if (!hasQueryParam && experiments.includes(flag)) {
      setFeatureEnabled(true);
    }
  }, [flag, experiments]);

  return featureEnabled;
}

/**
 * Enabled feature flags list.
 * @returns Feature flags names.
 */
export const useEnabledFeatures = () => {
  const [flags, setFlags] = useState<string[]>([]);

  useEffect(() => {
    setFlags(SUPPORTED_FEATURE_FLAGS.filter((feature) => Cookies.get(feature) === 'true'));
  }, []);

  return flags;
};

/**
 * Reset all feature flags.
 */
export const resetAllFeatureFlags = () =>
  SUPPORTED_FEATURE_FLAGS.forEach((feature) => {
    Cookies.remove(feature);
  });
